/**
 * BusinessSwitcher — Dashboard "Business OS" multi-business header (2026-07-31).
 *
 * A tenant owns many named businesses; this is the switcher at the top of the
 * Dashboard tab: pick the active business, create a new one (which auto-creates
 * its own project), rename, or delete. Modeled on the project switcher idiom in
 * projectStore (switch / create / rename / delete). The active business drives
 * the wizard + Build cards below it.
 */
import { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, Briefcase } from 'lucide-react';
import { useDashboardStore } from '@/stores/dashboardStore';

export function BusinessSwitcher() {
  const businesses = useDashboardStore((s) => s.businesses);
  const activeId = useDashboardStore((s) => s.activeBusinessId);
  const isSaving = useDashboardStore((s) => s.isSaving);
  const setActiveBusiness = useDashboardStore((s) => s.setActiveBusiness);
  const createBusiness = useDashboardStore((s) => s.createBusiness);
  const renameBusiness = useDashboardStore((s) => s.renameBusiness);
  const deleteBusiness = useDashboardStore((s) => s.deleteBusiness);

  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const active = businesses.find((b) => b.id === activeId);

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    await createBusiness(name);
    setNewName('');
    setCreating(false);
  };

  const submitRename = async () => {
    const name = renameValue.trim();
    if (name && active) await renameBusiness(active.id, name);
    setRenaming(false);
  };

  const confirmDelete = async () => {
    if (!active) return;
    if (window.confirm(`Delete business "${active.name}"? Its projects are kept.`)) {
      await deleteBusiness(active.id);
    }
  };

  return (
    <div
      data-testid="business-switcher"
      className="w-full max-w-2xl flex items-center gap-2 flex-wrap"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-vibe-accent/15 text-vibe-accent">
        <Briefcase className="h-4 w-4" />
      </span>

      {renaming && active ? (
        <div className="flex items-center gap-1.5">
          <input
            data-testid="business-rename-input"
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submitRename();
              if (e.key === 'Escape') setRenaming(false);
            }}
            className="rounded border border-vibe-border bg-vibe-bg-secondary px-2 py-1 text-sm text-vibe-text focus:outline-none focus:border-vibe-accent"
          />
          <button type="button" data-testid="business-rename-save" onClick={() => void submitRename()}
            className="text-vibe-accent hover:opacity-80"><Check className="h-4 w-4" /></button>
          <button type="button" onClick={() => setRenaming(false)}
            className="text-vibe-text-secondary hover:text-vibe-text"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <>
          <label htmlFor="business-select" className="sr-only">Active business</label>
          <select
            id="business-select"
            data-testid="business-select"
            value={activeId ?? ''}
            onChange={(e) => setActiveBusiness(Number(e.target.value))}
            className="rounded border border-vibe-border bg-vibe-bg-secondary px-2 py-1 text-sm text-vibe-text focus:outline-none focus:border-vibe-accent"
          >
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          {active && (
            <>
              <button
                type="button"
                data-testid="business-rename"
                title="Rename business"
                onClick={() => { setRenameValue(active.name); setRenaming(true); }}
                className="text-vibe-text-secondary hover:text-vibe-text"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                data-testid="business-delete"
                title="Delete business"
                onClick={() => void confirmDelete()}
                className="text-vibe-text-secondary hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </>
      )}

      <div className="ml-auto">
        {creating ? (
          <div className="flex items-center gap-1.5">
            <input
              data-testid="business-new-input"
              autoFocus
              placeholder="Business name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitCreate();
                if (e.key === 'Escape') { setCreating(false); setNewName(''); }
              }}
              className="rounded border border-vibe-border bg-vibe-bg-secondary px-2 py-1 text-sm text-vibe-text focus:outline-none focus:border-vibe-accent"
            />
            <button
              type="button"
              data-testid="business-new-save"
              disabled={isSaving || !newName.trim()}
              onClick={() => void submitCreate()}
              className="rounded bg-vibe-accent px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
            >
              {isSaving ? 'Creating…' : 'Create'}
            </button>
            <button type="button" onClick={() => { setCreating(false); setNewName(''); }}
              className="text-vibe-text-secondary hover:text-vibe-text"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="business-new"
            onClick={() => setCreating(true)}
            className="flex items-center gap-1.5 rounded border border-vibe-border px-3 py-1.5 text-sm text-vibe-text-secondary hover:text-vibe-text hover:border-vibe-accent transition-colors"
          >
            <Plus className="h-4 w-4" />
            New business
          </button>
        )}
      </div>
    </div>
  );
}

export default BusinessSwitcher;
